import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { TextFieldForm, Typography } from 'web-library';
import { InputAdornment } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { AeisExamFeeFormData } from './schema/AeisExamFeeSchema';
import { ExamFeeModelResponse } from '../../../../api-refactored/exam-configuration/usePostExamFeeListing';
import { SpersExamFeeFormData } from './schema/SpersExamFeeSchema';
import { fallbackNullNumber, getNumberFromString } from '../../../../utils/NumberUtils';

const useStyles = makeStyles()((theme) =>
({
  input: {
    '& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    paddingBottom: theme.spacing(3),
  },
  title: {
    color: theme.color.text.textPrimary,
    paddingBottom: theme.spacing(3),
  },
}));

interface RegistrationFeeFormSectionProps {
  title: string;
  formHook: UseFormReturn<AeisExamFeeFormData> | UseFormReturn<SpersExamFeeFormData>;
  isModalOpened: boolean;
  defaultData?: ExamFeeModelResponse;
}

const RegistrationFeeFormSection = ({
  title,
  formHook,
  isModalOpened,
  defaultData,
}: RegistrationFeeFormSectionProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { register, formState: { errors } } = formHook;
  const [registrationFee, setRegistrationFee] = useState<number | null>(null);
  const [lateRegistrationFee, setLateRegistrationFee] = useState<number | null>(null);

  useEffect(() => {
    if (isModalOpened) {
      // To reset the default value of the state every time modal is opened.
      if (defaultData) {
        setRegistrationFee(fallbackNullNumber(defaultData.registrationFee));
        setLateRegistrationFee(fallbackNullNumber(defaultData.lateRegistrationFee));
      } else {
        setRegistrationFee(null);
        setLateRegistrationFee(null);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isModalOpened]);

  return (
    <>
      <Typography variant="h3" className={classes.title}>
        {title}
      </Typography>
      <Typography variant="body2" className={classes.title}>
        {t('exam->>ExamFeeConfiguration.LateFeeInstruction')}
      </Typography>
      <TextFieldForm
        form={formHook}
        label={t('exam->>ExamFeeConfiguration.RegistrationFee')}
        name="registrationFee"
        placeholder="0.00"
        type="number"
        error={!!errors.registrationFee}
        helperText={errors?.registrationFee?.message?.toString()}
        inputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        fullWidth
        isRequired
        className={classes.input}
        value={registrationFee}
        // To limit user input to 2 decimal place
        onChange={(e) => {
          const val = getNumberFromString(e.target.value, 2);
          setRegistrationFee(val);
        }}
        onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
      />
      <TextFieldForm
          form={formHook}
        label={t('exam->>ExamFeeConfiguration.LateRegistrationFee')}
        name="lateRegistrationFee"
        placeholder="0.00"
        type="number"
        error={!!errors.lateRegistrationFee}
        helperText={errors?.lateRegistrationFee?.message?.toString()}
        inputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        fullWidth
        isRequired
        className={classes.input}
        value={lateRegistrationFee}
        // To limit user input to 2 decimal place
        onChange={(e) => {
          const val = getNumberFromString(e.target.value, 2);
          setLateRegistrationFee(val);
        }}
        onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
      />
    </>
  );
};

export default RegistrationFeeFormSection;
